import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dynamo } from '../../primary-record-types';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';

export type UseFastenConnectionsArgs = {
    medicalProfileId: string;
    enabled?: boolean;
    /** Poll interval in milliseconds, or a function that receives the query and returns an interval or false */
    refetchInterval?: number | ((query: any) => number | false);
    /** Callback when a connection is successfully disconnected */
    onDisconnectSuccess?: () => void;
};

export type UseFastenConnectionsResult = {
    isLoading: boolean;
    connections: Dynamo.FastenConnection[];
    refetch: () => void;
    disconnectConnection: (orgConnectionId: string) => void;
    isDisconnecting: boolean;
};

const useFastenConnections = (args: UseFastenConnectionsArgs): UseFastenConnectionsResult => {
    const { medicalProfileId, refetchInterval, onDisconnectSuccess } = args;
    const enabled = args?.enabled !== false;
    const queryClient = useQueryClient();

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['fasten-connections', medicalProfileId],
        queryFn: async () => {
            const response = await appAPI.get<{ connections: Dynamo.FastenConnection[] }>(
                urls.api.ehrIntegrations.fasten.endpoints.connections.list(medicalProfileId),
            );
            return response.connections;
        },
        enabled: enabled && !!medicalProfileId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchInterval,
    });

    const disconnectMutation = useMutation({
        mutationFn: async (orgConnectionId: string) => {
            await appAPI.del(
                urls.api.ehrIntegrations.fasten.endpoints.connections.disconnect(medicalProfileId, orgConnectionId),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['fasten-connections', medicalProfileId] });
            onDisconnectSuccess?.();
        },
    });

    return {
        isLoading,
        connections: data || [],
        refetch,
        disconnectConnection: disconnectMutation.mutate,
        isDisconnecting: disconnectMutation.isPending,
    };
};

export default useFastenConnections;
